import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react'

export type ScanStatus = 'received' | 'processing' | 'complete' | 'error'

export type VisionPageState = 'pending' | 'processing' | 'done'

export type ScanEntry = {
  scan_id: string
  file_name: string
  preview_url: string
  status: ScanStatus
  doc_id?: string | null
  title?: string
  description?: string
  doc_type?: string
  error?: string | null
  received_at: string
  // Progress fields populated by scan_progress SSE events.
  stage?: string
  stage_label?: string
  total_pages?: number | null
  chunks_total?: number | null
  chunks_embedded?: number | null
  // Parallel-vision grid state (populated by vision_page events).
  vision_total_pages?: number | null
  vision_pages?: Record<number, VisionPageState>
}

type State = {
  scans: ScanEntry[]
  currentIndex: number
  isOpen: boolean
  lastOpenedAt: number  // timestamp; entries received after this time are "unread"
}

type Action =
  | { type: 'ADD_SCAN'; entry: ScanEntry }
  | { type: 'UPDATE_SCAN'; scan_id: string; patch: Partial<ScanEntry> }
  | { type: 'VISION_BATCH'; scan_id: string; total_pages: number }
  | { type: 'VISION_PAGE'; scan_id: string; page: number; state: VisionPageState }
  | { type: 'GO_NEXT' }
  | { type: 'GO_PREV' }
  | { type: 'CLOSE' }
  | { type: 'REOPEN' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_SCAN': {
      const exists = state.scans.some(s => s.scan_id === action.entry.scan_id)
      if (exists) return state
      const scans = [...state.scans, action.entry]
      // Always open the panel and focus the new scan. Previously we kept
      // the current index when the panel was already open, which meant a
      // freshly-arrived scan stayed hidden behind older ones in focus.
      return {
        ...state,
        scans,
        currentIndex: scans.length - 1,
        isOpen: true,
        lastOpenedAt: Date.now(),
      }
    }
    case 'UPDATE_SCAN': {
      const scans = state.scans.map(s =>
        s.scan_id === action.scan_id ? { ...s, ...action.patch } : s
      )
      return { ...state, scans }
    }
    case 'VISION_BATCH': {
      const scans = state.scans.map(s => {
        if (s.scan_id !== action.scan_id) return s
        const pages: Record<number, VisionPageState> = {}
        for (let i = 1; i <= action.total_pages; i++) pages[i] = 'pending'
        return { ...s, vision_total_pages: action.total_pages, vision_pages: pages }
      })
      return { ...state, scans }
    }
    case 'VISION_PAGE': {
      const scans = state.scans.map(s => {
        if (s.scan_id !== action.scan_id) return s
        const prev = s.vision_pages ?? {}
        return {
          ...s,
          vision_pages: { ...prev, [action.page]: action.state },
        }
      })
      return { ...state, scans }
    }
    case 'GO_NEXT':
      return { ...state, currentIndex: Math.min(state.currentIndex + 1, state.scans.length - 1) }
    case 'GO_PREV':
      return { ...state, currentIndex: Math.max(state.currentIndex - 1, 0) }
    case 'CLOSE':
      return { ...state, isOpen: false }
    case 'REOPEN':
      return { ...state, isOpen: true, currentIndex: 0, lastOpenedAt: Date.now() }
    default:
      return state
  }
}

const initialState: State = {
  scans: [],
  currentIndex: 0,
  isOpen: false,
  lastOpenedAt: 0,
}

type ContextValue = State & {
  addScan: (entry: ScanEntry) => void
  updateScan: (scan_id: string, patch: Partial<ScanEntry>) => void
  setVisionBatch: (scan_id: string, total_pages: number) => void
  setVisionPage: (scan_id: string, page: number, state: VisionPageState) => void
  goToNext: () => void
  goToPrev: () => void
  closePanel: () => void
  reopenPanel: () => void
  unreadCount: number
  current: ScanEntry | null
}

const ScanPanelContext = createContext<ContextValue | null>(null)

export function ScanPanelProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const addScan = useCallback((entry: ScanEntry) => dispatch({ type: 'ADD_SCAN', entry }), [])
  const updateScan = useCallback(
    (scan_id: string, patch: Partial<ScanEntry>) => dispatch({ type: 'UPDATE_SCAN', scan_id, patch }),
    []
  )
  const setVisionBatch = useCallback(
    (scan_id: string, total_pages: number) =>
      dispatch({ type: 'VISION_BATCH', scan_id, total_pages }),
    []
  )
  const setVisionPage = useCallback(
    (scan_id: string, page: number, state: VisionPageState) =>
      dispatch({ type: 'VISION_PAGE', scan_id, page, state }),
    []
  )
  const goToNext = useCallback(() => dispatch({ type: 'GO_NEXT' }), [])
  const goToPrev = useCallback(() => dispatch({ type: 'GO_PREV' }), [])
  const closePanel = useCallback(() => dispatch({ type: 'CLOSE' }), [])
  const reopenPanel = useCallback(() => dispatch({ type: 'REOPEN' }), [])

  const value = useMemo<ContextValue>(() => {
    const unreadCount = state.scans.filter(
      s => new Date(s.received_at).getTime() > state.lastOpenedAt
    ).length
    const current = state.scans[state.currentIndex] ?? null
    return {
      ...state,
      addScan,
      updateScan,
      setVisionBatch,
      setVisionPage,
      goToNext,
      goToPrev,
      closePanel,
      reopenPanel,
      unreadCount,
      current,
    }
  }, [state, addScan, updateScan, setVisionBatch, setVisionPage, goToNext, goToPrev, closePanel, reopenPanel])

  return <ScanPanelContext.Provider value={value}>{children}</ScanPanelContext.Provider>
}

export function useScanPanel(): ContextValue {
  const ctx = useContext(ScanPanelContext)
  if (!ctx) throw new Error('useScanPanel must be used inside ScanPanelProvider')
  return ctx
}
